import moment from 'moment'
import { required, email } from 'vuelidate/lib/validators'
import { mapGetters, mapActions } from 'vuex'
import * as components from 'src/components'
import { oneOf } from 'src/utils'
import { Constants } from 'src/constants'
import recaptchaService from 'src/recaptcha'

const RETRY_SECONDS = 10

export default {
  components: {
    ...components
  },
  computed: {
    ...mapGetters({
      context: 'passport/context',
      user: 'passport/user'
    })
  },
  data () {
    return {
      form: {
        login: ''
      },
      confirmationForm: {
        code: ''
      },
      error: null,
      isErrorVisible: false,
      isBusy: false,
      isConfirmationBusy: false,
      isConfirmationVisible: false,
      isConfirmationErrorVisible: false,
      confirmationError: null,
      forgotRetryDetails: null,
      isResendCode: false,
      emailConfirmationCodeLength: Constants.confirmation.emailConfirmationCodeLength,
      colorTheme: Constants.colorTheme
    }
  },
  mounted () {
    const { login } = this.$route.query
    if (login != null) {
      this.form.login = login
    }
  },
  validations: {
    form: {
      login: {
        required,
        email
      }
    },
    confirmationForm: {
      code: {
        required,
        isComplex () {
          return this.confirmationForm.code.length === this.emailConfirmationCodeLength
        }
      }
    }
  },
  watch: {
    'confirmationForm.code': {
      async handler () {
        if (!this.isResendCode && this.confirmationForm.code.length === this.emailConfirmationCodeLength) {
          await this.handleConfirm()
        }
      }
    }
  },
  methods: {
    ...mapActions({
      signup: 'passport/signup',
      confirmRecoverEmailPasswordByCode: 'passport/confirmRecoverEmailPasswordByCode',
      forgot: 'passport/forgot',
      describeError: 'errors/describeError'
    }),
    oneOf,
    checkCode () {
      this.$v.confirmationForm.$touch()
      if (!this.$v.confirmationForm.$invalid) {
        this.handleConfirm()
      }
    },
    confirmationCode (value) {
      this.confirmationForm.code = value
      if (value.length === this.emailConfirmationCodeLength) {
        this.$v.form.$touch()
        if (!this.$v.confirmationForm.$invalid) {
          this.handleConfirm()
        }
      }
    },
    confirmCode () {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        this.handleSubmit()
      }
    },
    handleBack () {
      this.$router.push({path: '/security/signin'})
    },
    handleLogin (value) {
      this.form.login = value
    },
    async handleSubmit () {
      if (this.forgotRetryDetails != null) {
        return
      }
      this.isBusy = true
      this.isErrorVisible = false
      this.error = null
      setImmediate(async () => {
        try {
          await this.forgot({
            ...this.form,
          })
          this.isConfirmationVisible = true
          this.forgotRetryDetails = {
            createdAt: moment(),
            countdown: RETRY_SECONDS,
            iterval: null,
            timeout: setTimeout(() => {
              if (this.forgotRetryDetails) {
                if (this.forgotRetryDetails.interval) {
                  clearInterval(this.forgotRetryDetails.interval)
                }
                this.forgotRetryDetails = null
              }
            }, 1000 * RETRY_SECONDS)
          }
          this.forgotRetryDetails.interval = setInterval(() => {
            this.forgotRetryDetails.countdown = Math.max(
              0,
              RETRY_SECONDS - moment().diff(this.forgotRetryDetails.createdAt, 'seconds')
            )
          }, 200)
        } catch (e) {
          const { errorMessage } = await this.describeError({ error: e })
          this.error = errorMessage
          this.isErrorVisible = true
        } finally {
          this.isBusy = false
        }
      })
    },
    async handleResend () {
      return this.handleSubmit()
    },
    async handleConfirm () {
      if (this.isConfirmationBusy) {
        return
      }

      this.isConfirmationBusy = true
      this.isConfirmationErrorVisible = false
      this.error = null
      setImmediate(async () => {
        try {
          const recaptchaToken = await recaptchaService.getToken()

          await this.confirmRecoverEmailPasswordByCode({
            code: this.confirmationForm.code,
            recaptchaToken
          })
          setImmediate(() => {
            this.$router.push('/security/passwd')
          })
        } catch (e) {
          const { errorMessage } = await this.describeError({ error: e })
          this.confirmationError = errorMessage
          this.isConfirmationErrorVisible = true
        } finally {
          this.isConfirmationBusy = false
          this.isResendCode = true
        }
      })
    }
  }
}
